import React, { useState, useEffect, useContext, useRef } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams, useLocation, useHistory } from "react-router-dom";
import EntityMarkSeries from './EntityMarkSeries';
import FolderResultsPanel from '../OverviewPanel/FolderResultsPanel';
import IndexResultsPanel from '../OverviewPanel/IndexResultsPanel';
import LevelCrumbs from '../LevelCrumbs';
import { getEntityToColumnIncidenceMap } from 'Utils';
import AppContext from 'Utils/AppContext';
import { filterTablesForDisplay } from 'Utils/entityLevels';
import FetchContainer from 'Utils/FetchContainer';
import {
  getOverview,
  clearDiscoverResultsCache,
  buildEntityTablesFromOverview,
  buildTableSampledRowCountsMapForEntityTables,
  buildTableColumnEntityMapFromOverview,
} from 'DataDiscovery/discoverApi';

const NavTitleWrapper = ({ children, ...props }) => {
  const history = useHistory();

  return (
    <FetchContainer fetchingData={props.loading} absolute={true}>
      <div data-testid="results-details-tab">
        <Row className="widget-row">
          <Col>
            <LevelCrumbs
              activeCrumb='entity'
              path={'/results'}
              searchid={props.searchid}
              entity={props.entity}
              connection={props.connection}
              resetFilter={() => history.push(`/results/${props.searchid}/ALL`)}
            />
          </Col>
        </Row>
        <Row className='widget-row text-center'>
          <Col>
            <h2 className='sg-eyebrow'><span className='text-uppercase'>{props.dataLabel}</span> WITH SENSITIVE DATA</h2>
          </Col>
        </Row>
        <div>{ children }</div>
      </div>
    </FetchContainer>
  );
}

const EntityPanel = (props) => {
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [entityTables, setEntityTables] = useState({});
  const [from, setFrom] = useState(0);
  const [tablesToSampledRows, setTablesToSampledRows] = useState({});
  const [entityToColumnIncidenceMap, setEntityToColumnIncidenceMap] = useState({});
  const [overviewTables, setOverviewTables] = useState([]);
  const [folderIndex, setFolderIndex] = useState(null);
  const [tableIndex, setTableIndex] = useState(null);
  const { state } = useLocation();
  const { entityLevels } = useContext(AppContext);
  const searchid = params.search;
  const setError = props.setError;
  const entity = params.entity ? params.entity : 'ALL' ;
  const inProgress = props.inProgress;
  const isEmbedded = props.isEmbedded ? true : false;

  const fetchDataHandler = async (searchid) => {
    try {
      const response = await getOverview(searchid);
      // Table-index (PeopleSoft-80K) runs carry a summary index instead of per-table
      // rows: render the index-backed scatter scoped to this entity (it fetches its own
      // top-N page server-side, filtered by entity=).
      if (response?.tableIndex) {
        setTableIndex(response.tableIndex);
        setFolderIndex(null);
        setOverviewTables([]);
        setEntityTables({});
        return;
      }
      setTableIndex(null);
      // Folder Search runs carry a folder index instead of per-file rows: render the
      // folder-grouped panel scoped to this entity (it lazy-fetches chunks per folder).
      if (response?.folders) {
        setFolderIndex(response.folders);
        setOverviewTables([]);
        setEntityTables({});
        return;
      }
      setFolderIndex(null);
      // Drop HIDDEN entities install-wide before transforms (see OverviewPanel for rationale).
      const tables = filterTablesForDisplay(response?.tables ?? [], entityLevels);
      const tableSampledRowCountsMap = buildTableSampledRowCountsMapForEntityTables(tables);
      const tableColumnEntityMap = buildTableColumnEntityMapFromOverview(tables);
      const entityToColumnIncidenceMapValue = getEntityToColumnIncidenceMap(tableSampledRowCountsMap, tableColumnEntityMap);
      const entityTablesValue = buildEntityTablesFromOverview(tables);

      setEntityToColumnIncidenceMap(entityToColumnIncidenceMapValue);
      setTablesToSampledRows(tableSampledRowCountsMap);
      setEntityTables(entityTablesValue);
      setOverviewTables(tables);
    } catch (e) {
      console.log(e);
      setError(e?.message);
    }
  };

  // partialTick bumps when ReportPanel's slow partial-results poll lands a fresh snapshot;
  // the refetch reads the results cache that poll just warmed (no extra payload transfer).
  const partialTick = props.partialTick;
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchDataHandler(searchid);
      setLoading(false);
    };
    if (searchid) init();
  }, [searchid, entity, partialTick]);

  // Do NOT poll the full overview while the run is in progress — each fetch re-downloads and
  // re-parses the whole multi-MB payload (see OverviewPanel for the full rationale, audit R6 /
  // §3 UI). Fetch exactly once when the run transitions out of progress; the App-level status
  // poll keeps progress live in the meantime.
  const wasInProgress = useRef(false);
  useEffect(() => {
    if (wasInProgress.current && !inProgress && searchid) {
      clearDiscoverResultsCache(searchid);
      fetchDataHandler(searchid);
    }
    wasInProgress.current = !!inProgress;
  }, [inProgress, searchid]);

  const entityPanelContent = tableIndex ? (
    <Row>
      <Container>
        <Row className="widget-row">
          <Col>
            <IndexResultsPanel
              searchid={searchid}
              dataLabel={props.dataLabel}
              entity={entity}
              totalTables={tableIndex.total_tables}
              partialTick={partialTick}
            />
          </Col>
        </Row>
      </Container>
    </Row>
  ) : folderIndex ? (
    <Row>
      <Container>
        <Row className="widget-row">
          <Col>
            <FolderResultsPanel
              searchid={searchid}
              dataLabel={props.dataLabel}
              folders={folderIndex}
              entity={entity}
              partialTick={partialTick}
            />
          </Col>
        </Row>
      </Container>
    </Row>
  ) : (
    <Row>
      <Container>
        <Row className="widget-row">
          <Col>
            <EntityMarkSeries
              searchid={searchid}
              lowConfidence={state && state.lowConfidence}
              dataLabel={props.dataLabel}
              overviewTables={overviewTables}
              entityTables={(state && !state.lowConfidence) ? entityTables : entityTables['LOW_INCIDENCE_FINDINGS']}
              entity={entity}
              isEncrypted={props.isEncrypted}
              encryptedTables={props.encryptedTables}
              loading={loading}
              tablesToSampledRows={tablesToSampledRows}
              entityToColumnIncidenceMap={entityToColumnIncidenceMap}
            />
          </Col>
        </Row>
      </Container>
    </Row>
  );
  
  return isEmbedded ? (
    entityPanelContent
  ) : (     
    <NavTitleWrapper
      dataLabel={props.dataLabel}
      searchid={searchid}
      entity={entity}
      connection={props.connection}
      loading={loading}
    >
      {entityPanelContent}
    </NavTitleWrapper>

  );
}

export default EntityPanel;
